import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Calendar, ArrowLeft, Users, TrendingUp,
  DollarSign, ShoppingBag, Activity, ExternalLink
} from 'lucide-react';

const DayDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // استقبال البيانات من الـ state
  const dayData = location.state?.dayData;

  // إذا لم توجد بيانات، ارجع للصفحة الرئيسية
  if (!dayData) {
    navigate('/profit-margin');
    return null;
  }

  const StatCard = ({ title, value, icon: Icon, gradient, textColor }) => (
    <div className={`bg-gradient-to-br ${gradient} p-6 rounded-2xl border border-gray-700/50 shadow-xl`}>
      <div className="flex items-center justify-between mb-4">
        <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl">
          <Icon className={`w-6 h-6 ${textColor}`} />
        </div>
      </div>
      <div>
        <p className="text-gray-300 text-sm font-medium mb-2">{title}</p>
        <p className="text-2xl font-bold text-white">
          ${typeof value === 'number' ? value.toFixed(2) : '0.00'}
        </p>
      </div>
    </div>
  );

  const handleAgentClick = (agentName) => {
    navigate(`/profit-margin/agent/${agentName}/${dayData.date}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate('/profit-margin')}
              className="p-2 sm:p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-xl border border-gray-700/50 transition-all duration-300 group flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white transition-colors" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                تفاصيل يوم {new Date(dayData.date).toLocaleDateString('ar-EG')}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg flex items-center gap-2 mt-1 sm:mt-2 flex-wrap">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm lg:text-base">
                  تحليل مفصل لأداء اليوم
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
            <span className="text-gray-300 font-medium text-sm sm:text-base">
              {new Date(dayData.date).toLocaleDateString('ar-EG', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <StatCard
            title="إجمالي الربح"
            value={dayData.total_profit}
            icon={TrendingUp}
            gradient="from-green-600/20 to-emerald-800/20"
            textColor="text-green-400"
          />
          <StatCard
            title="إجمالي السعر"
            value={dayData.total_price}
            icon={DollarSign}
            gradient="from-blue-600/20 to-blue-800/20"
            textColor="text-blue-400"
          />
          <StatCard
            title="السعر الأساسي"
            value={dayData.base_price}
            icon={DollarSign}
            gradient="from-purple-600/20 to-purple-800/20"
            textColor="text-purple-400"
          />
          <StatCard
            title="عدد الطلبات"
            value={dayData.orders_count}
            icon={ShoppingBag}
            gradient="from-orange-600/20 to-orange-800/20"
            textColor="text-orange-400"
          />
        </div>

        {/* Agents Statistics */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl">
          <div className="p-6 border-b border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">إحصائيات الوكلاء</h2>
            </div>
          </div>

          <div className="p-6">
            {dayData.agent_statistics && dayData.agent_statistics.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {dayData.agent_statistics.map((agent, index) => (
                  <div 
                    key={agent.agent_name} 
                    className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 p-6 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-300 cursor-pointer group"
                    onClick={() => handleAgentClick(agent.agent_name)}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' :
                        index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white' :
                        index === 2 ? 'bg-gradient-to-r from-amber-600 to-yellow-600 text-white' :
                        'bg-gray-600 text-gray-300'
                      }`}>
                        #{index + 1}
                      </div>
                      <h4 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">
                        {agent.agent_name}
                      </h4>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors ml-auto" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-900/50 p-3 rounded-lg group-hover:bg-gray-700 transition-colors">
                        <p className="text-xs text-gray-400 mb-1">السعر الأساسي</p>
                        <p className="text-lg font-bold text-emerald-400">
                          ${agent.total_base_price.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-gray-900/50 p-3 rounded-lg group-hover:bg-gray-700 transition-colors">
                        <p className="text-xs text-gray-400 mb-1">عدد الطلبات</p>
                        <p className="text-lg font-bold text-blue-400">{agent.orders_count}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="bg-gray-700/50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-400 text-lg">لا توجد إحصائيات وكلاء لهذا اليوم</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayDetails;